import { useCallback, useEffect, useState } from "react";
import { TITLE_SORTS, type TitleSort } from "./types";

/**
 * The portal's path router - a lookup with two parameters, so no dependency
 * (the same reasoning as src/family/routes.tsx).
 *
 * Real paths, because several of them are entered from OUTSIDE the SPA on a
 * cold load and must resolve with no client state: the mail links
 * (`/account/confirm#token=…`, `/account/reset-password#token=…`, `/login`,
 * `/account/forgot-password` - `services/consumerAccountMail.ts`
 * PORTAL_PATHS). Pages' `_redirects` sends every path to index.html. The
 * retired shop's addresses (`/offers/…`, `/checkout/…`) are not-found.
 *
 * Tokens travel in the URL FRAGMENT, which a browser never sends to a server
 * and never puts in a Referer, and the screens that read one strip it from
 * the address bar before they spend it.
 */
export type Route =
  /** The store page; `q` = an active search (`/?q=`), "" = the rails. */
  | { name: "browse"; q: string }
  /** One genre's titles (`/genre/:id`), or every title (`/all`). */
  | { name: "genre"; genreId: string | null; sort: TitleSort | null }
  | { name: "title"; assetId: string }
  | { name: "my" }
  | { name: "plan" }
  | { name: "account" }
  | { name: "login"; next: string | null }
  | { name: "register" }
  | { name: "confirm" }
  | { name: "resend" }
  | { name: "forgot" }
  | { name: "reset" }
  | { name: "invite" }
  /** The portal's Information rail as a page (members only). */
  | { name: "information" }
  /** One Information item: a clip or a PDF, read in the page. */
  | { name: "information-item"; id: string }
  /** The player: full window, no portal chrome. */
  | { name: "watch"; assetId: string }
  | { name: "not-found" };

/** Only same-origin paths are honoured as a post-login destination. */
export function safeNext(v: string | null): string | null {
  if (!v || !v.startsWith("/") || v.startsWith("//")) return null;
  return v;
}

export function resolve(pathname: string, search: string): Route {
  const path = pathname.replace(/\/+$/, "") || "/";
  const q = new URLSearchParams(search);
  switch (path) {
    case "/":
      return { name: "browse", q: q.get("q") ?? "" };
    case "/all":
      return { name: "genre", genreId: null, sort: sortParam(q.get("sort")) };
    case "/my":
      return { name: "my" };
    case "/plan":
      return { name: "plan" };
    case "/account":
      return { name: "account" };
    case "/login":
    case "/account/login":
      return { name: "login", next: safeNext(q.get("next")) };
    case "/register":
    case "/account/register":
      return { name: "register" };
    case "/account/confirm":
      return { name: "confirm" };
    case "/account/resend":
      return { name: "resend" };
    case "/account/forgot-password":
      return { name: "forgot" };
    case "/account/reset-password":
      return { name: "reset" };
    case "/invite":
      return { name: "invite" };
    case "/information":
      return { name: "information" };
  }
  const info = /^\/information\/([^/]+)$/.exec(path);
  if (info) {
    return { name: "information-item", id: decodeURIComponent(info[1]!) };
  }
  const watch = /^\/watch\/([^/]+)$/.exec(path);
  if (watch) return { name: "watch", assetId: decodeURIComponent(watch[1]!) };
  const title = /^\/titles\/([^/]+)$/.exec(path);
  if (title) return { name: "title", assetId: decodeURIComponent(title[1]!) };
  const genre = /^\/genre\/([^/]+)$/.exec(path);
  if (genre) {
    return {
      name: "genre",
      genreId: decodeURIComponent(genre[1]!),
      sort: sortParam(q.get("sort")),
    };
  }
  return { name: "not-found" };
}

function sortParam(v: string | null): TitleSort | null {
  return v && (TITLE_SORTS as readonly string[]).includes(v)
    ? (v as TitleSort)
    : null;
}

/** The path of a genre page (null = every title), with its sort. */
export function genrePath(genreId: string | null, sort?: TitleSort | null) {
  const base =
    genreId === null ? "/all" : `/genre/${encodeURIComponent(genreId)}`;
  return sort && sort !== "popular" ? `${base}?sort=${sort}` : base;
}

/** The `token` in `#token=…`, or "" when there is none. */
export function fragmentToken(hash: string = window.location.hash): string {
  const params = new URLSearchParams(hash.replace(/^#/, ""));
  return params.get("token") ?? "";
}

/** Drop the fragment (the spent token) from the address bar and history. */
export function stripFragment(): void {
  if (!window.location.hash) return;
  window.history.replaceState(
    window.history.state,
    "",
    window.location.pathname + window.location.search
  );
}

/**
 * What the portal keeps on each of ITS history entries (`history.state`):
 *
 *   idx      - how many in-app pages lie behind this one. A page loaded
 *              from outside (a typed address, a mail link)
 *              has no state and counts as 0, so "Tillbaka" knows when
 *              `history.back()` would leave the portal.
 *   entered  - the path this entry was pushed with (`replace` keeps it):
 *              tells a search started on another page (`/?q=` pushed) from
 *              one typed on the store itself (`/` replaced by `/?q=`).
 *   scrollY  - where the page was scrolled when the visitor left it, put
 *              back when they return (`popstate`).
 *
 * A browser keeps `history.state` across a reload, so all three survive one.
 */
interface EntryState {
  idx: number;
  entered?: string;
  scrollY?: number;
  /** The in-app page this entry was opened FROM (path + query). */
  from?: string;
}

export function entryState(): EntryState {
  const raw = window.history.state as Partial<EntryState> | null;
  return {
    idx: typeof raw?.idx === "number" && raw.idx > 0 ? raw.idx : 0,
    entered: typeof raw?.entered === "string" ? raw.entered : undefined,
    scrollY: typeof raw?.scrollY === "number" ? raw.scrollY : undefined,
    from: typeof raw?.from === "string" ? raw.from : undefined,
  };
}

/**
 * Where "Tillbaka" goes when there is no in-app page behind this one: the
 * page's natural parent: the player's is its title; else the store.
 */
export function parentPath(route: Route): string {
  switch (route.name) {
    case "watch":
      return `/titles/${encodeURIComponent(route.assetId)}`;
    case "information-item":
      return "/information";
    default:
      return "/";
  }
}

/** Is there a back control on this page? Everywhere but the store's home. */
export function hasBack(route: Route): boolean {
  // The player draws its own back control over the picture.
  if (route.name === "watch") return false;
  return !(route.name === "browse" && !route.q);
}

export interface Navigation {
  route: Route;
  go(path: string): void;
  replace(path: string): void;
  /**
   * "Tillbaka": the previous in-app page when there is one (the browser's
   * own back, so scroll and the address bar agree), else `parentPath`.
   * Search results typed on the store go back to the store's rails.
   */
  back(): void;
}

function current(): Route {
  return resolve(window.location.pathname, window.location.search);
}

function scrollY(): number {
  return typeof window.scrollY === "number" ? window.scrollY : 0;
}

/**
 * Put the page back where it was. The screens load their data after the
 * route changes, so the page may not be tall enough yet: keep trying for a
 * moment, and stop at once if the visitor scrolls, types or touches.
 */
function restoreScroll(y: number): void {
  if (typeof window.scrollTo !== "function") return;
  if (y <= 0 || typeof window.requestAnimationFrame !== "function") {
    window.scrollTo(0, Math.max(0, y));
    return;
  }
  const until = Date.now() + 2500;
  const events = ["wheel", "touchstart", "keydown", "mousedown"] as const;
  let stopped = false;
  const stop = () => {
    stopped = true;
  };
  const done = () => {
    for (const e of events) window.removeEventListener(e, stop);
  };
  for (const e of events) {
    window.addEventListener(e, stop, { passive: true, once: true });
  }
  const step = () => {
    if (stopped) return done();
    window.scrollTo(0, y);
    if (Math.abs(scrollY() - y) < 2 || Date.now() > until) return done();
    window.requestAnimationFrame(step);
  };
  window.requestAnimationFrame(step);
}

export function useNavigation(): Navigation {
  const [route, setRoute] = useState<Route>(current);

  useEffect(() => {
    // The portal puts the scroll back itself, once the page has content.
    try {
      window.history.scrollRestoration = "manual";
    } catch {
      /* not supported: the browser's own guess stands */
    }
    const onPop = () => {
      setRoute(current());
      restoreScroll(entryState().scrollY ?? 0);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const go = useCallback((path: string) => {
    const here = entryState();
    // Remember where this page was scrolled, for the way back.
    window.history.replaceState({ ...here, scrollY: scrollY() }, "");
    window.history.pushState(
      {
        idx: here.idx + 1,
        entered: path,
        from: window.location.pathname + window.location.search,
      } satisfies EntryState,
      "",
      path
    );
    setRoute(current());
    window.scrollTo?.(0, 0);
  }, []);

  const replace = useCallback((path: string) => {
    const here = entryState();
    window.history.replaceState(
      {
        idx: here.idx,
        entered: here.entered,
        from: here.from,
      } satisfies EntryState,
      "",
      path
    );
    setRoute(current());
  }, []);

  const back = useCallback(() => {
    const here = entryState();
    const now = current();
    const searchStartedElsewhere = !!here.entered?.startsWith("/?");
    if (now.name === "browse" && now.q && !searchStartedElsewhere) {
      replace("/");
      return;
    }
    if (here.idx > 0) window.history.back();
    else go(parentPath(now));
  }, [go, replace]);

  return { route, go, replace, back };
}
